import { useState, useEffect } from "react";
import axios from "axios";
import { FolderCog, Cloud, HardDrive, Save, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StorageConfig() {
  const [config, setConfig] = useState({
    storage_type: "local",
    client_id: "",
    tenant_id: "",
    is_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/storage`);
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/config/storage`, config);
      toast.success("Storage configuration saved");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Storage Configuration
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Configure where session recordings and files are stored
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="stats-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FolderCog className="w-5 h-5 text-primary" />
              Storage Type
            </CardTitle>
            <CardDescription>
              Choose where to store presentation files, recordings, and reports
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <RadioGroup
              value={config.storage_type}
              onValueChange={(value) => setConfig({ ...config, storage_type: value })}
              className="space-y-4"
            >
              <div 
                className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  config.storage_type === 'local' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setConfig({ ...config, storage_type: 'local' })}
              >
                <RadioGroupItem value="local" id="local" data-testid="storage-local" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-5 h-5 text-primary" />
                    <Label htmlFor="local" className="text-base font-semibold cursor-pointer">
                      Local Storage
                    </Label>
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1">
                    Store files locally on the server. Best for testing and development.
                  </p>
                </div>
                {config.storage_type === 'local' && (
                  <CheckCircle className="w-5 h-5 text-primary" />
                )}
              </div>

              <div 
                className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  config.storage_type === 'onedrive' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setConfig({ ...config, storage_type: 'onedrive' })}
              >
                <RadioGroupItem value="onedrive" id="onedrive" data-testid="storage-onedrive" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-[#0078D4]" />
                    <Label htmlFor="onedrive" className="text-base font-semibold cursor-pointer">
                      Microsoft OneDrive
                    </Label>
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1">
                    Store files in organization's OneDrive. Requires Azure AD configuration.
                  </p>
                </div>
                {config.storage_type === 'onedrive' && (
                  <CheckCircle className="w-5 h-5 text-primary" />
                )}
              </div>
            </RadioGroup>

            {config.storage_type === 'onedrive' && (
              <div className="space-y-4 pt-4 border-t border-border animate-fade-in">
                <h3 className="font-semibold text-[#1A1A1A]">Azure AD Configuration</h3>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="client_id">Application (Client) ID</Label>
                    <Input
                      id="client_id"
                      data-testid="onedrive-client-id"
                      placeholder="Enter your Azure App Client ID"
                      value={config.client_id || ""}
                      onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="tenant_id">Directory (Tenant) ID</Label>
                    <Input
                      id="tenant_id"
                      data-testid="onedrive-tenant-id"
                      placeholder="Enter your Azure Tenant ID"
                      value={config.tenant_id || ""}
                      onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_secret">Client Secret</Label>
                    <Input
                      id="client_secret"
                      data-testid="onedrive-client-secret"
                      type="password"
                      placeholder="Enter your Azure App Client Secret"
                      value={config.client_secret || ""}
                      onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                    />
                  </div>
                </div>
                {config.is_configured && (
                  <div className="p-4 rounded-lg bg-[#DCFCE7] border border-[#86EFAC] flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                    <p className="text-sm text-[#166534]">
                      OneDrive is configured and ready to use
                    </p>
                  </div>
                )}
                <div className="p-4 rounded-lg bg-[#FFF7ED] border border-[#FDBA74]">
                  <p className="text-sm text-[#9A3412]">
                    <strong>Note:</strong> You'll need to register an app in Azure Portal and configure proper permissions for Microsoft Graph API to enable OneDrive integration.
                  </p>
                </div>
              </div>
            )}

            {config.storage_type === 'local' && (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 animate-fade-in">
                <p className="text-sm text-primary">
                  <strong>Folder Structure:</strong> Files will be stored in{" "}
                  <code className="px-2 py-1 bg-white rounded text-xs">
                    /uploads/[session_id]/
                  </code>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            data-testid="save-config-btn"
            disabled={saving}
            className="bg-primary hover:bg-primary/90 rounded-full px-8"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
